import { useNavigate } from "react-router-dom";
import { Leaf, ArrowRight, Sparkles, ShieldCheck, Clock, HeartPulse, ClipboardList, Star, Lock } from "lucide-react";
import { trackLandingCtaClick } from "../lib/analytics";

const CTA = ({ label = "Start Your Free Assessment", testid, className = "" }) => {
  const navigate = useNavigate();
  return (
    <button
      data-testid={testid}
      onClick={() => { trackLandingCtaClick(); navigate("/questionnaire"); }}
      className={`inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-emerald-600 hover:from-emerald-700 hover:to-emerald-800 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl shadow-emerald-600/25 transition-all transform hover:scale-[1.02] uppercase tracking-wide ${className}`}
    >
      {label} <ArrowRight className="w-5 h-5" />
    </button>
  );
};

const TESTIMONIALS = [
  { name: "Sarah M.", text: "The recommendations felt genuinely personal — not a generic list. I finally know where to start." },
  { name: "James T.", text: "Took two minutes and the safety notes around my medication were exactly what I needed." },
  { name: "Amara O.", text: "Clear, practical and free. The recipes were easy to follow from day one." },
];

export default function FreeAssessmentLanding() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50/40">
      {/* Minimal logo bar — no nav distractions */}
      <div className="flex items-center justify-center py-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-gradient-to-br from-primary to-emerald-700 rounded-xl flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <span className="font-playfair text-xl font-bold text-gray-900">Elite Herbalist</span>
        </div>
      </div>

      {/* HERO */}
      <section className="max-w-3xl mx-auto px-4 text-center pt-6 pb-12">
        <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 text-xs font-bold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wide">
          <Sparkles className="w-3.5 h-3.5" /> 100% Free · No account needed
        </div>
        <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5" data-testid="landing-headline">
          Your Free Personalised{" "}
          <span className="bg-gradient-to-r from-primary via-emerald-600 to-emerald-700 bg-clip-text text-transparent" style={{ WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Herbal Assessment
          </span>
        </h1>
        <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl mx-auto mb-8">
          Answer a few quick questions about your wellness goals and lifestyle, and receive herbal
          recommendations matched to your individual profile — in under 3 minutes.
        </p>
        <CTA testid="landing-hero-cta" />
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 mt-5 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-primary" /> Under 3 minutes</span>
          <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-primary" /> Safety-aware guidance</span>
          <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-primary" /> Your details stay private</span>
        </div>
        <p className="text-xs text-gray-500 mt-4" data-testid="landing-disclaimer">
          Educational wellness guidance. Not medical diagnosis or treatment.
        </p>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-4xl mx-auto px-4 pb-14">
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { icon: HeartPulse, title: "Personalised to You", text: "Built around your goals, age, lifestyle and any medications — never one-size-fits-all." },
            { icon: ShieldCheck, title: "Safety First", text: "Every recommendation includes appropriate use, precautions and important considerations." },
            { icon: ClipboardList, title: "Practical & Actionable", text: "Preparation methods and simple recipes you can start using today." },
          ].map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-white rounded-3xl border-2 border-gray-100 p-6 text-center shadow-sm">
              <div className="w-12 h-12 mx-auto bg-gradient-to-br from-primary to-emerald-600 rounded-2xl flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-playfair text-lg font-bold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0F3D2E] py-14">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-white mb-8">How It Works</h2>
          <div className="grid sm:grid-cols-3 gap-6 mb-10">
            {[
              { n: 1, t: "Tell us about yourself", d: "Your goals, lifestyle and relevant considerations." },
              { n: 2, t: "We match your profile", d: "Our recommendation system identifies relevant herbal wellness options." },
              { n: 3, t: "Get your recommendations", d: "Personalised herbs, preparations and safety guidance — instantly." },
            ].map((s) => (
              <div key={s.n} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <p className="text-emerald-300 font-black text-xs uppercase tracking-widest mb-2">Step {s.n}</p>
                <h3 className="text-white font-bold mb-2">{s.t}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <CTA testid="landing-mid-cta" />
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="max-w-4xl mx-auto px-4 py-14">
        <div className="grid sm:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="bg-white rounded-3xl border-2 border-gray-100 p-6 shadow-sm">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">"{t.text}"</p>
              <p className="text-xs font-bold text-gray-500">{t.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA + TRUST */}
      <section className="max-w-2xl mx-auto px-4 pb-16 text-center">
        <h2 className="font-playfair text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Ready for wellness guidance made for you?
        </h2>
        <p className="text-gray-600 mb-7">Free. Personalised. No account required.</p>
        <CTA testid="landing-bottom-cta" />
        <div className="mt-10 text-xs text-gray-500 leading-relaxed space-y-2">
          <p>Educational wellness guidance. Not medical diagnosis or treatment. Always consult a qualified
            healthcare professional before starting any herbal regimen, especially if you are pregnant,
            nursing, taking medication or managing a health condition.</p>
          <p>Your details are stored securely, used only to deliver your recommendations and wellness
            emails, and never sold. Unsubscribe anytime.</p>
          <p>© {new Date().getFullYear()} Elite Herbalist</p>
        </div>
      </section>
    </div>
  );
}
